import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

import { useCallback, useEffect, useState } from 'react';
import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { Typography } from '../../ui';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import ToolbarPlugin from './plugins/toolbar-plugin';
import { twMerge } from 'tailwind-merge';
import { cardListStyles } from '../../molecules/cardList/variants';
import { richTextEditorConfig } from './richt-text-editor.config';
import { RichTextEditorAction } from '../../atoms/rich-text-editor-action/rich-text-editor-action';
import { IconFile, IconPaperclip } from '@maia/icons';
import { AnimatePresence, motion } from 'motion/react';
import ListMaxIndentLevelPlugin from './plugins/list-max-indent-level-plugin';
import { DropZone, FileDropItem, FileTrigger } from 'react-aria-components';
import { RichTextEditorFileList } from '../../molecules/rich-text-editor-file-list/rich-text-editor-file-list';
import { EditorListenerPlugin } from './plugins/listener-plugin';
import AutoLinkPlugin from './plugins/auto-link-plugin';

const placeholder = 'Voeg aantekening toe';

type Props = {
  isDisabled?: boolean;
  contentClassname?: string;
  className?: string;
  isSubmitForForm?: boolean;
  onSubmit?: (markdown: string, files: Array<File>) => void;
  onChange?: (markdown: string) => void;
  onChangeFiles?: (files: Array<File>) => void;
};

export function RichTextEditor(props: Props) {
  const {
    className,
    contentClassname,
    isDisabled,
    isSubmitForForm,
    onSubmit,
    onChange,
    onChangeFiles,
  } = props;

  const [files, setFiles] = useState<Array<File>>([]);

  function handleRemoveFile(fileToRemove: File) {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  }

  const handleChangeFiles = useCallback(
    (files: Array<File>) => {
      if (onChangeFiles) {
        onChangeFiles(files);
      }
    },
    [onChangeFiles],
  );

  useEffect(() => {
    handleChangeFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <DropZone
      className={({ isDropTarget }) =>
        twMerge(
          cardListStyles({ variant: 'elevated' }),
          'relative border border-grayscale100 overflow-hidden',
          isDropTarget && 'border-info300 border-dashed',
          className,
        )
      }
      onDrop={async (e) => {
        const droppedItems: Array<FileDropItem> = e.items.filter(
          (item) => item.kind === 'file',
        );

        const droppedFiles = await Promise.all(
          droppedItems.map((item) => item.getFile()),
        );

        setFiles([...files, ...droppedFiles]);
      }}
    >
      {({ isDropTarget }) => (
        <LexicalComposer initialConfig={richTextEditorConfig}>
          <ToolbarPlugin />

          <div className="relative">
            <AnimatePresence>
              {isDropTarget && (
                <motion.div
                  className="absolute inset-0 bg-grayscale0/80 z-10 flex items-center justify-center flex-col"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IconFile className="fill-grayscale600 min-w-24 w-24 h-24" />
                  <Typography
                    variant="body-m"
                    weight="regular"
                    className="fill-grayscale600"
                  >
                    Voeg bestand toe
                  </Typography>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="relative">
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    className={twMerge(
                      'px-16 min-h-80 outline-none pt-16 text-grayscale600 text-xs md:text-sm leading-1 md:leading-2 font-extralight',
                      contentClassname,
                    )}
                    aria-placeholder={placeholder}
                    placeholder={
                      <Typography
                        as="div"
                        variant="body-s"
                        className="overflow-hidden absolute text-ellipsis top-16 left-16 select-none inline-block pointer-events-none text-grayscale300"
                      >
                        {placeholder}
                      </Typography>
                    }
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <TabIndentationPlugin />
              <HistoryPlugin />
              <AutoFocusPlugin />
              <ListPlugin />
              <LinkPlugin />
              <AutoLinkPlugin />
              <ListMaxIndentLevelPlugin maxDepth={7} />
              <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
              {onChange && (
                <OnChangePlugin
                  onChange={(e) => {
                    e.read(() => {
                      const markdown = $convertToMarkdownString(TRANSFORMERS);
                      onChange(markdown);
                    });
                  }}
                />
              )}
            </div>

            <div className="flex items-center gap-x-12 justify-between px-16 py-16">
              <RichTextEditorFileList
                files={files}
                onDelete={handleRemoveFile}
                className="overflow-x-auto"
              />

              <div className="flex flex-row items-center gap-x-16">
                <FileTrigger
                  allowsMultiple
                  onSelect={(e) => {
                    if (e) {
                      const selectedFiles = Array.from(e);
                      setFiles([...files, ...selectedFiles]);
                    }
                  }}
                >
                  <RichTextEditorAction icon={IconPaperclip} color="blue" />
                </FileTrigger>

                {(onSubmit || isSubmitForForm) && (
                  <EditorListenerPlugin
                    isDisabled={isDisabled}
                    onSubmit={
                      onSubmit
                        ? (markdown) => {
                            onSubmit(markdown, files);
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </LexicalComposer>
      )}
    </DropZone>
  );
}
